// File: frontend/src/components/BulkBusinessRelationshipTool.js
import React, { useState } from 'react';
import { X, Link as LinkIcon, Upload, AlertCircle, Check, HelpCircle, Plus } from 'lucide-react';
import { peopleAPI } from '../utils/api';

const BulkBusinessRelationshipTool = ({ onClose, people, businesses, onComplete }) => {
  const [mode, setMode] = useState('interactive');
  const [csvData, setCsvData] = useState('');
  const [rows, setRows] = useState([{ personId: '', businessId: '', note: '' }]);
  const [showHelp, setShowHelp] = useState(false);

  const addRow = () => setRows([...rows, { personId: '', businessId: '', note: '' }]);
  const removeRow = (i) => setRows(rows.filter((_, idx) => idx !== i));
  const updateRow = (i, field, value) => {
    const next = [...rows];
    next[i][field] = value;
    setRows(next);
  };

  const getPersonByName = (name) => {
    const n = name.trim().toLowerCase();
    return people.find(p => {
      const full = `${p.first_name || ''} ${p.last_name || ''}`.toLowerCase().trim();
      return full === n || p.first_name?.toLowerCase() === n ||
        `${p.last_name}, ${p.first_name}`.toLowerCase() === n;
    });
  };

  const getBusinessByName = (name) => {
    const n = name.trim().toLowerCase();
    return businesses.find(b => b.name.toLowerCase() === n);
  };

  const applyLinks = async (links) => {
    let created = 0;
    const errors = [];
    for (const { personId, businessId, note } of links) {
      try {
        await peopleAPI.setPrimaryBusiness(personId, businessId || null);
        created++;
      } catch (e) {
        errors.push(note ? `${note}: ${e.message}` : e.message);
      }
    }
    return { created, errors };
  };

  const submitInteractive = async () => {
    const valid = rows.filter(r => r.personId && r.businessId);
    if (valid.length === 0) { alert('Please add at least one valid row'); return; }
    const { created, errors } = await applyLinks(
      valid.map(r => ({ personId: parseInt(r.personId), businessId: parseInt(r.businessId), note: r.note }))
    );
    alert(`Done!\nLinked: ${created}\nErrors: ${errors.length}${errors.length ? '\n' + errors.join('\n') : ''}`);
    if (created > 0 && onComplete) onComplete();
    if (errors.length === 0) onClose();
  };

  const submitCSV = async () => {
    const lines = csvData.trim().split('\n');
    if (lines.length < 2) { alert('CSV needs a header row + at least one data row'); return; }
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase());
    const links = [];
    const parseErrors = [];

    for (let i = 1; i < lines.length; i++) {
      const vals = lines[i].split(',').map(v => v.trim());
      const row = {};
      headers.forEach((h, idx) => { row[h] = vals[idx] || ''; });

      const personName = row['person'] || row['from'] || row['name'] || '';
      const bizName = row['business'] || row['to'] || row['company'] || '';
      const note = row['note'] || row['notes'] || '';

      if (!personName || !bizName) { parseErrors.push(`Row ${i + 1}: missing person or business`); continue; }

      const person = getPersonByName(personName);
      const biz = getBusinessByName(bizName);

      if (!person) { parseErrors.push(`Row ${i + 1}: person not found: ${personName}`); continue; }
      if (!biz) { parseErrors.push(`Row ${i + 1}: business not found: ${bizName}`); continue; }

      links.push({ personId: person.id, businessId: biz.id, note });
    }

    if (parseErrors.length > 0 && links.length === 0) {
      alert('Errors:\n' + parseErrors.join('\n')); return;
    }

    const { created, errors } = await applyLinks(links);
    alert(`Done!\nLinked: ${created}\nErrors: ${[...parseErrors, ...errors].length}${parseErrors.length ? '\nParse:\n' + parseErrors.join('\n') : ''}${errors.length ? '\nAPI:\n' + errors.join('\n') : ''}`);
    if (created > 0 && onComplete) onComplete();
    if (errors.length === 0 && parseErrors.length === 0) onClose();
  };

  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 shadow-xl rounded-lg p-6 max-w-4xl w-full max-h-[90vh] overflow-auto">

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-green-600 rounded-lg">
              <LinkIcon className="w-6 h-6 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-900 dark:text-slate-100">Bulk Create Relationships</h2>
              <p className="text-sm text-gray-600 dark:text-gray-400">Link multiple people to businesses at once</p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <button onClick={() => setShowHelp(!showHelp)} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
              <HelpCircle className="w-5 h-5" />
            </button>
            <button onClick={onClose} className="p-2 text-gray-500 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Help */}
        {showHelp && (
          <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 mb-6 text-sm text-blue-800 dark:text-blue-300">
            <p className="font-semibold mb-2">How to use:</p>
            <p><strong>Interactive:</strong> Select person and business from dropdowns, then click Create.</p>
            <p className="mt-1"><strong>CSV:</strong> Paste data with columns: <code>Person, Business, Note</code></p>
            <p className="text-xs mt-2 flex items-center gap-1"><AlertCircle className="w-3 h-3 shrink-0" /> Names must match exactly (case-insensitive).</p>
          </div>
        )}

        {/* Mode Toggle */}
        <div className="flex items-center space-x-2 mb-6">
          {['interactive', 'csv'].map(m => (
            <button key={m} onClick={() => setMode(m)}
              className={`px-4 py-2 rounded-lg transition-all flex items-center space-x-2 ${mode === m ? 'bg-green-600 text-white' : 'bg-gray-100 dark:bg-slate-700 text-gray-700 dark:text-slate-300 hover:bg-gray-200 dark:hover:bg-slate-600'}`}>
              {m === 'csv' && <Upload className="w-4 h-4" />}
              <span className="capitalize">{m === 'csv' ? 'CSV Import' : 'Interactive'}</span>
            </button>
          ))}
        </div>

        {/* Interactive */}
        {mode === 'interactive' && (
          <div className="space-y-4">
            <div className="grid grid-cols-12 gap-3 text-xs font-semibold text-gray-600 dark:text-slate-400 uppercase tracking-wider px-1 mb-1">
              <div className="col-span-5">Person</div>
              <div className="col-span-5">Business</div>
              <div className="col-span-1">Note</div>
              <div className="col-span-1" />
            </div>

            <div className="space-y-3 max-h-96 overflow-y-auto pr-1">
              {rows.map((row, i) => (
                <div key={i} className="grid grid-cols-12 gap-3 items-center">
                  <select value={row.personId} onChange={e => updateRow(i, 'personId', e.target.value)}
                    className="col-span-5 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100 focus:ring-2 focus:ring-green-500">
                    <option value="">Select person…</option>
                    {people.map(p => (
                      <option key={p.id} value={p.id}>{p.first_name} {p.last_name}</option>
                    ))}
                  </select>

                  <select value={row.businessId} onChange={e => updateRow(i, 'businessId', e.target.value)}
                    className="col-span-5 px-3 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100 focus:ring-2 focus:ring-green-500">
                    <option value="">Select business…</option>
                    {businesses.map(b => (
                      <option key={b.id} value={b.id}>{b.name}</option>
                    ))}
                  </select>

                  <input type="text" value={row.note} onChange={e => updateRow(i, 'note', e.target.value)}
                    placeholder="Note"
                    className="col-span-1 px-2 py-2 border border-gray-300 dark:border-slate-600 rounded-lg text-sm dark:bg-slate-700 dark:text-gray-100 focus:ring-2 focus:ring-green-500" />

                  <button onClick={() => removeRow(i)} className="col-span-1 p-2 text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg">
                    <X className="w-4 h-4" />
                  </button>
                </div>
              ))}
            </div>

            <button onClick={addRow}
              className="w-full px-4 py-2 border-2 border-dashed border-gray-300 dark:border-slate-600 rounded-lg text-gray-500 dark:text-slate-400 hover:border-green-400 hover:text-green-600 transition-all flex items-center justify-center space-x-2">
              <Plus className="w-4 h-4" /><span>Add Row</span>
            </button>
          </div>
        )}

        {/* CSV */}
        {mode === 'csv' && (
          <div className="space-y-4">
            <div className="bg-gray-50 dark:bg-slate-900 border border-gray-200 dark:border-slate-700 rounded-lg p-3 text-sm font-mono text-gray-700 dark:text-gray-300">
              <div className="font-semibold mb-1">Format:</div>
              <div>Person, Business, Note</div>
              <div className="text-xs text-gray-500 mt-1">Example:</div>
              <div className="text-xs">John Doe, Acme Corp, CEO</div>
              <div className="text-xs">Jane Smith, Acme Corp, Engineer</div>
            </div>
            <textarea value={csvData} onChange={e => setCsvData(e.target.value)}
              className="w-full h-64 px-4 py-3 border border-gray-300 dark:border-slate-600 rounded-lg font-mono text-sm focus:ring-2 focus:ring-green-500 dark:bg-slate-700 dark:text-gray-100"
              placeholder="Paste CSV here…&#10;Person, Business, Note&#10;John Doe, Acme Corp, CEO" />
            <div className="flex items-center space-x-2 text-sm text-gray-500">
              <AlertCircle className="w-4 h-4" />
              <span>{Math.max(0, csvData.split('\n').filter(l => l.trim()).length - 1)} relationships to create</span>
            </div>
          </div>
        )}

        {/* Actions */}
        <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200 dark:border-slate-700">
          <button onClick={onClose} className="px-6 py-2 text-gray-700 dark:text-slate-300 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-all">
            Cancel
          </button>
          <button onClick={mode === 'interactive' ? submitInteractive : submitCSV}
            className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center space-x-2">
            <Check className="w-4 h-4" /><span>Create Relationships</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default BulkBusinessRelationshipTool;
